import { tt } from '@agos/shared'

/**
 * A franchise's restaurant settings, as the Control Tower sees them.
 *
 * **FB-S04 — `phoneNumber` is not the number customers see.** The customer footer applies a fallback
 * this row does not know about: `highlightedPhones` wins when set, and it is set. Measured,
 * `phone_number` is `0901 123 456` while the footer answers `0910 406 434`. So a super-admin correcting
 * the number here changes a field nobody is shown, and the save succeeds.
 *
 * The screen therefore shows **both**, names which one reaches customers, and does not quietly write to
 * the other — moving where an edit lands would be a second surprise rather than a fix.
 *
 * **The three switches are the ones FB-C06 is about.** `requireOpenToPublic()` checks `maintenanceMode`
 * and `onlineOrdersEnabled` and **not the opening hours**, which are compared only for a scheduled
 * pre-order. `autoAccept` being off is the only thing standing between a 03:00 order and the kitchen.
 * An operator switching orders on here should know the hours are not a third switch.
 */

export interface ManagerSettings {
  maintenanceMode: boolean
  onlineOrdersEnabled: boolean
  autoAccept: boolean
  announcementEnabled: boolean
  announcement: string | null
  /** `restaurant_settings.phone_number` — the losing side of the footer's fallback. */
  phoneNumber: string | null
  footerAddress: string | null
}

export function managerSettingsOf(payload: unknown): ManagerSettings {
  const outer = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const row = (typeof outer.settings === 'object' && outer.settings !== null ? outer.settings : outer) as Record<string, unknown>
  const text = (key: string): string | null => {
    const value = row[key]
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
  }
  return {
    maintenanceMode: row.maintenanceMode === true,
    onlineOrdersEnabled: row.onlineOrdersEnabled === true,
    autoAccept: row.autoAccept === true,
    announcementEnabled: row.announcementEnabled === true,
    announcement: text('announcement'),
    phoneNumber: text('phoneNumber'),
    footerAddress: text('footerAddress'),
  }
}

/** Whether each delivery platform's key is configured, as presence and never as a value. */
export interface DeliveryPlatformKeys {
  wolt: boolean
  bolt: boolean
}

/**
 * The two delivery-platform keys, reported as set or not - §3.58.
 *
 * **The one genuine gap §3.53's whole queue produced beyond what was already built.** The Tower
 * draws a credential toggle for every other integration and drew none for these two, although
 * `TenantManagerSettingsService` masks them into exactly the right shape already (`hasWoltApiKey`,
 * `hasBoltApiKey`, lines 221-222) - so this needs nothing behind it.
 *
 * **And the reason it is these two and not a generic reader.** §3.57 made the integrations screen
 * draw *every* scalar its endpoint answers, which is safe there because every credential arrives
 * `{hasValue}`-shaped. **This endpoint is not safe for that**: it carries `hourlyRates` in plain
 * form - *"every employee's pay"*, `BACKEND.md:8183` - so the same technique here would publish a
 * franchise's payroll to the platform operator. Two named booleans, deliberately.
 *
 * Read from wherever the response nests them, because `managerSettingsOf` above already has to cope
 * with both an enveloped and a bare shape.
 */
export function deliveryPlatformKeysOf(payload: unknown): DeliveryPlatformKeys {
  const outer = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const row = (typeof outer.settings === 'object' && outer.settings !== null ? outer.settings : outer) as Record<string, unknown>
  const integrations = (typeof row.integrationsSettings === 'object' && row.integrationsSettings !== null
    ? row.integrationsSettings
    : {}) as Record<string, unknown>
  return {
    wolt: integrations.hasWoltApiKey === true,
    bolt: integrations.hasBoltApiKey === true,
  }
}

/** The state in words, which a boolean cannot carry on a screen. */
export function platformKeyNote(configured: boolean): string {
  return configured ? tt('kľúč nastavený') : tt('kľúč nenastavený')
}

/** Whether the franchise is taking web orders at all, by the two switches the server actually checks. */
export function acceptsWebOrders(settings: ManagerSettings): boolean {
  return !settings.maintenanceMode && settings.onlineOrdersEnabled
}

/**
 * Said beside the switches — FB-C06 from the operator's side.
 *
 * The hours look like a third switch and are not one: nothing checks them for an immediate order.
 */
export function hoursNotEnforcedNote(settings: ManagerSettings): string {
  if (!acceptsWebOrders(settings)) return ''
  return settings.autoAccept
    ? tt('Otváracie hodiny sa pri okamžitej objednávke nekontrolujú a objednávky sa prijímajú automaticky.')
    : tt('Otváracie hodiny sa pri okamžitej objednávke nekontrolujú - objednávka mimo hodín čaká v stave prijatá.')
}

/**
 * Whether the number on this screen is the one customers are shown.
 *
 * It cannot be answered from this payload alone — the footer's value has to be compared — so the caller
 * passes it in. Guessing would be how the wrong number gets called the right one.
 */
export function phoneMatchesFooter(settings: ManagerSettings, footerPhone: string | null): boolean {
  if (settings.phoneNumber === null || footerPhone === null) return false
  return settings.phoneNumber.replace(/\s/g, '') === footerPhone.replace(/\s/g, '')
}

export function phoneMismatchNote(settings: ManagerSettings, footerPhone: string | null): string {
  if (footerPhone === null) return ''
  if (phoneMatchesFooter(settings, footerPhone)) return ''
  return `${tt('Zákazníci vidia')}: ${footerPhone}`
}

/**
 * What a save sends - SAAS.md §3.5, the owner's A14.
 *
 * FB-505's line for this page was one word: **read-only**. `PATCH …/manager-settings` existed with no
 * caller, and `TenantManagerSettingsService.update` writes the keys the body names - so the same rule
 * as the franchise patch applies and for a sharper reason here: two of these fields are **switches
 * that stop a restaurant taking orders**. Sending an unchanged `maintenanceMode` would be a no-op
 * until the moment somebody toggled it in the manager between this page loading and the save, at
 * which point it would silently put it back.
 */
export function managerSettingsPatch(
  draft: ManagerSettings,
  original: ManagerSettings,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  const keys = [
    'maintenanceMode',
    'onlineOrdersEnabled',
    'autoAccept',
    'announcementEnabled',
    'announcement',
    'phoneNumber',
    'footerAddress',
  ] as const
  for (const key of keys) {
    const next = draft[key]
    if (next !== original[key]) {
      // A blank text field is sent as `null`, which is how the read reports *not set* - sending `""`
      // would store an empty string that then reads back as absent anyway, with a difference only a
      // query could see.
      patch[key] = typeof next === 'string' && next.trim() === '' ? null : next
    }
  }
  return patch
}

/**
 * Said beside the two switches, because this page is not the one whose restaurant it is.
 *
 * A super-admin turning maintenance on is taking somebody else's shop offline from another building.
 * The manager's own screen has the same switches and the person there can see the queue; this one
 * cannot, so it says so rather than assuming the operator remembers.
 */
export function remoteSwitchNote(): string {
  return tt('Toto vypína príjem objednávok v prevádzke. Personál o tom nedostane upozornenie.')
}

/**
 * A franchise's whole configuration, group by group - SAAS.md §3.99.
 *
 * **Eleven groups the server has always written and no Tower screen could reach.**
 * `TenantManagerSettingsService.BLOBS` names all eleven; legacy's own super-admin has a
 * `JSONB_SECTIONS` table over exactly the same eleven, described as a *"Power-user editor - neplatný
 * JSON odmietnutý serverom"*; the AGOS screen offered the six scalars above this comment and nothing
 * else.
 *
 * **`hourlyRates` is not here, and that is the one deliberate difference from legacy.** §3.58
 * declined to publish this endpoint's fields generically because it *"carries `hourlyRates` in plain
 * form - every employee's pay - so the same technique here would publish a franchise's payroll to
 * the platform operator"*, and that reasoning did not stop being true when the editor arrived. Ten
 * rates on Šurany. Banked as `OWNER-QUESTIONS.md` #34 rather than decided here, because it is about
 * people's pay and they are the owner's restaurants.
 *
 * `billingSettings` **is** here: the restaurant's IČO, DIČ and IBAN is a business identity that
 * appears on every invoice it issues and that the Tower's invoicing screens already carry.
 */
export interface SettingsSection {
  /** The payload key, which is also the `PATCH` key. */
  key: string
  label: string
  /** Legacy's own one-liner, translated where legacy wrote a column name. */
  description: string
}

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    key: 'openingHours',
    label: tt('Otváracie hodiny'),
    description: tt('Po-Ne plán, núdzové zatvorenie, oznámenia.'),
  },
  {
    key: 'basicSettings',
    label: tt('Základné'),
    description: tt('GA/Pixel ID, adresa depa, doprava podľa vzdialenosti, vlastný kód.'),
  },
  {
    key: 'systemSettingsJson',
    label: tt('Systémové'),
    description: tt('Prepínače funkcií a systémové nastavenia.'),
  },
  {
    key: 'socialSettings',
    label: tt('Social a web'),
    description: tt('Logo, Facebook, Instagram, TikTok, Google recenzie, mapa.'),
  },
  {
    key: 'billingSettings',
    label: tt('Fakturačné údaje prevádzky'),
    description: tt('IČO, DIČ a IBAN reštaurácie a nastavenia faktúr jej zákazníkom.'),
  },
  {
    key: 'franchiseSettings',
    label: tt('Nastavenia franšízy'),
    description: tt('Názov aplikácie, režim fakturácie, centrálne riadenie, kontakt na partnera.'),
  },
  {
    key: 'designSettings',
    label: tt('Dizajn'),
    description: tt('Svetlý či tmavý režim, akcent, font, výška loga.'),
  },
  {
    key: 'pizzaConfig',
    label: tt('Pizza konfigurácia'),
    description: tt('Nastavenia modulu na skladanie pizze.'),
  },
  {
    key: 'lunchConfig',
    label: tt('Obedové menu'),
    description: tt('Nastavenia obedového menu.'),
  },
  {
    key: 'integrationsSettings',
    label: tt('Wolt a Bolt'),
    description: tt('Radšej cez obrazovku Integrácie - kľúče sú tam ako polia.'),
  },
  /* §3.109, answering `OWNER-QUESTIONS.md` #34 - **the eleventh section, and the only reason it was
   * not here is that it is people's pay.** §3.99 built ten of eleven and named this omission with its
   * reason; the owner was asked and said *"Yes, show them like the other ten"*.
   *
   * **Keyed by employee id, not by role** - FB-101 records a client written from the old summary that
   * would have paid everybody by role. The stored blob is `{"1": 0, "7": 5.5, …}` and legacy reads it
   * as `hourlyRates[employee.id]`, so the description says so rather than leaving an operator to guess
   * whose 5,5 they are editing. */
  {
    key: 'hourlyRates',
    label: tt('Hodinové mzdy'),
    description: tt('Hodinová mzda podľa ID zamestnanca, nie podľa role.'),
  },
]

/**
 * Each section's stored value, as the JSON an operator edits.
 *
 * **Two spaces and sorted-as-stored**, because the alternative is one line of 900 characters that
 * nobody can edit without breaking it. A group the response does not carry reads as `{}` rather than
 * as empty text: `{}` is what the server will store, and a blank textarea beside a label saying
 * *Pizza konfigurácia* looks like a failed load.
 */
export function settingsSectionsOf(payload: unknown): Record<string, string> {
  const outer = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const row = (typeof outer.settings === 'object' && outer.settings !== null ? outer.settings : outer) as Record<string, unknown>
  const text: Record<string, string> = {}
  for (const section of SETTINGS_SECTIONS) {
    const value = row[section.key]
    text[section.key] =
      value === undefined || value === null ? '{}' : JSON.stringify(value, null, 2)
  }
  return text
}

/**
 * Why this section will not save, or `''`.
 *
 * **Checked here rather than by the server, which is where legacy leaves it.** `update` writes
 * `CAST(:field AS jsonb)`, so a missing brace comes back as a Postgres syntax error naming a
 * character offset - true, and not something an operator can act on. Legacy's own subtitle admits
 * the same: *neplatný JSON odmietnutý serverom*.
 */
export function settingsSectionProblem(text: string): string {
  const trimmed = text.trim()
  if (trimmed === '') return tt('Prázdne - napíšte aspoň {}.')
  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    return tt('Neplatný JSON.')
  }
  // **An object, not any JSON.** Every one of these columns holds an object, and
  // `keepingSecrets` only merges a secret back when what arrived `instanceof Map` - so a bare array
  // or a number would be stored, and for `basicSettings` it would take the Anthropic key with it.
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return tt('Musí to byť objekt v { }.')
  }
  return ''
}

/**
 * What a save sends: the sections that were edited, and nothing else - §3.5's rule, §3.99.
 *
 * **`PATCH …/manager-settings` replaces a blob whole**, so sending all ten would overwrite groups a
 * franchise's own manager may have changed since this page loaded. For `openingHours` that would move
 * a restaurant's trading hours, which is why this is a rule here rather than tidiness.
 *
 * Compared by **parsed value**, not by text - and the reason is narrower than it first looks, which
 * is worth writing down because the first version of this comment was wrong and a mutation proved
 * it. It claimed the pretty-printer alone makes every section's text differ from its stored string,
 * so a text comparison would send all ten on the first save. **It would not**: both sides of this
 * comparison come out of `settingsSectionsOf`, so an untouched section is the *same string* on each
 * side and `!==` is equivalent there. What the parsed comparison actually buys is the operator who
 * re-indents or reflows a section **by hand** and saves - a whole-blob write for no change at all.
 * Smaller, real, and not what was claimed.
 */
export function settingsSectionsPatch(
  edited: Readonly<Record<string, string>>,
  stored: Readonly<Record<string, string>>,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  for (const section of SETTINGS_SECTIONS) {
    const next = edited[section.key]
    if (next === undefined) continue
    if (settingsSectionProblem(next) !== '') continue
    const before = stored[section.key] ?? '{}'
    let parsedNext: unknown
    let parsedBefore: unknown
    try {
      parsedNext = JSON.parse(next)
      parsedBefore = JSON.parse(before)
    } catch {
      continue
    }
    if (JSON.stringify(parsedNext) !== JSON.stringify(parsedBefore)) {
      patch[section.key] = parsedNext
    }
  }
  return patch
}

/** Every section that will not parse, so one message can name them rather than ten. */
export function unparseableSections(
  edited: Readonly<Record<string, string>>,
): { key: string; label: string; problem: string }[] {
  return SETTINGS_SECTIONS.flatMap((section) => {
    const text = edited[section.key]
    if (text === undefined) return []
    const problem = settingsSectionProblem(text)
    return problem === '' ? [] : [{ key: section.key, label: section.label, problem }]
  })
}

/**
 * Said above the sections, because a blob write is not like a field write.
 *
 * A super-admin editing `openingHours` here is retyping a restaurant's whole week from another
 * building, and the group is replaced rather than merged - a key left out is a key removed.
 */
export function rawSectionNote(): string {
  return tt(
    'Sekcia sa ukladá celá - kľúč, ktorý tu nie je, sa zmaže. Ukladajú sa len sekcie, ktoré ste zmenili.',
  )
}

/**
 * What the wage-rate section is, said where it is edited - `SAAS.md §3.116`.
 *
 * **This function used to say the opposite and §3.109 left it saying it.** Its words were *"Hodinové
 * mzdy sa tu nezobrazujú - nastavujú sa v manažéri prevádzky."* - correct while the section was held
 * back for owner question #34, and **false from the moment they answered it**. §3.109 added the
 * eleventh section, updated three tests and did not touch the sentence, so the screen listed *Hodinové
 * mzdy* and printed a line underneath saying it did not.
 *
 * **The e2e is what found it**, on a whole-suite run: `tower.spec.ts:1537` asserted ten sections and
 * this note's presence, in a case whose own comment read *"pending owner question #34"*. A screen
 * contradicting itself in two adjacent elements is not something a unit test of either one can see -
 * `agos-a-test-on-one-side-of-a-seam`, and `agos-a-rename-empties-a-source-pinned-guard` for the
 * sentence that outlived its premise.
 *
 * **It says something useful rather than being deleted.** The blob is keyed by **employee id** -
 * `{"1": 0, "7": 5.5, …}`, which is how legacy reads it - and FB-101 records a client written from the
 * old summary that would have paid everybody by role. An operator editing raw JSON here needs that
 * fact more than they need the section's name repeated.
 */
export function hourlyRatesNote(): string {
  return tt('Hodinové mzdy sú kľúčované ID zamestnanca, nie rolou - napríklad {"7": 5.5}.')
}
